import { useI18n } from 'vue-i18n'
import {
  faFile,
  faFileAudio,
  faFileCode,
  faFileExcel,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFileZipper,
} from '@fortawesome/duotone-regular-svg-icons'

const mimeTypeMap: Record<string, string[]> = {
  pdf: ['application/pdf', 'application/x-pdf', 'application/acrobat', 'application/vnd.adobe.acrobat'],
  word: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/rtf', 'text/rtf', 'application/vnd.oasis.opendocument.text'],
  excel: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/vnd.oasis.opendocument.spreadsheet'],
  video: ['video/webm', 'video/mpeg', 'video/ogg', 'video/x-msvideo', 'video/mp4', 'video/quicktime', 'video/x-flv', 'video/x-matroska', 'video/x-ms-wmv', 'video/3gpp'],
  audio: ['audio/webm', 'audio/wav', 'audio/ogg', 'audio/mpeg', 'audio/midi', 'audio/x-midi', 'video/x-msvideo', 'audio/aac', 'audio/flac', 'audio/x-flac', 'audio/opus', 'audio/x-wav', 'audio/mp4'],
  image: ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/tiff', 'image/webp', 'image/bmp', 'image/heic', 'image/heif', 'image/avif'],
  powerpoint: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.oasis.opendocument.presentation'],
  archive: ['application/zip', 'application/x-7z-compressed', 'application/gzip', 'application/x-bzip', 'application/x-bzip2', 'application/x-bzip', 'application/x-zip-compressed'],
  python: ['text/x-python', 'text/x-python3', 'text/x-script.python', 'text/x-script.python3', 'text/x-python-script'],
  code: ['text/html', 'text/xml', 'text/css', 'text/javascript', 'application/x-javascript', 'application/javascript', 'application/json', 'application/ld+json', 'application/xml', 'application/x-typescript'],
  text: ['text/plain', 'text/markdown'],
}

function useFileInfo() {
  const { t } = useI18n()

  function readableFileSize(bytes: number) {
    return bytes > 1024 * 1024
      ? `${Math.round(bytes / 1024 / 1024)} MB`
      : bytes >= 1024
        ? `${Math.round(bytes / 1024)} kB`
        : `${bytes} bytes`
  }

  function readableMimeType(mimeType: string) {
    mimeType = mimeType.toLowerCase()
    for (const key in mimeTypeMap) {
      if (mimeTypeMap[key].includes(mimeType)) {
        return t(`mimeType.${key}`)
      }
    }
    return ''
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeTypeMap.image.includes(mimeType)) {
      return faFileImage
    }
    if (mimeTypeMap.audio.includes(mimeType)) {
      return faFileAudio
    }
    if (mimeTypeMap.video.includes(mimeType)) {
      return faFileVideo
    }
    if (mimeTypeMap.pdf.includes(mimeType)) {
      return faFilePdf
    }
    if (mimeTypeMap.word.includes(mimeType)) {
      return faFileWord
    }
    if (mimeTypeMap.excel.includes(mimeType)) {
      return faFileExcel
    }
    if (mimeTypeMap.powerpoint.includes(mimeType)) {
      return faFilePowerpoint
    }
    if (mimeTypeMap.code.includes(mimeType)) {
      return faFileCode
    }
    if (mimeTypeMap.archive.includes(mimeType)) {
      return faFileZipper
    }
    if (mimeTypeMap.python.includes(mimeType)) {
      return faFileCode
    }
    if (mimeTypeMap.text.includes(mimeType)) {
      return faFileLines
    }
    return faFile
  }

  return {
    mimeTypeMap,
    readableFileSize,
    readableMimeType,
    getFileIcon,
  }
}

export default useFileInfo
